import React from 'react';

import {
  RadioGroupOption,
  LegacyText,
  Grid,
  Popover,
  IconButton,
  Typography,
  SettingsIcon,
} from '@gogame/components';
import { withForm, FormField } from '@gogame/form';

import { useIsBreakdownMode } from '../../hooks';
import { useChartState } from '../../hooks';
import { SettingsBlocksWrapper } from './components';
import {
  CHART_SETTINGS_OUTPUT,
  CHART_SETTINGS_TREND,
  CHART_SETTINGS_SCALE,
  CHART_SETTINGS_LOOKBACK_MODE,
  CHART_SETTINGS_INCLUDE_CHANGE_RATE,
  CHART_SETTINGS_CHART_GROUPING,
  CHART_SETTINGS_CHANGE_RATE_MODE,
  CHART_SETTINGS_WEEK_START_DAY,
  START_DAY_MON,
  weekStartOptions,
  SCALE_WEEKLY,
  scaleOptions,
  depositsAggregationOptions,
  lookbackOptions,
  outputOptions,
  changeRateOptions,
} from './constants';

export const SettingsForm = withForm({
  enableReinitialize: true,
})(({ values, submitForm, initialValues, setFieldValue, dirty, disabled }) => {
  const isChartBreakdownMode = useIsBreakdownMode();
  const chartState = useChartState();
  const pieChartMode = chartState?.chartViewSettings?.type === 'pie';

  const isChangeRateVisible = () => {
    return values[CHART_SETTINGS_OUTPUT] === 'growth';
  };

  const isWeekStartDayVisible = () => {
    return values[CHART_SETTINGS_SCALE] !== SCALE_WEEKLY;
  };

  const handleOutputChange = () => {
    if (isChangeRateVisible) {
      setFieldValue(CHART_SETTINGS_CHANGE_RATE_MODE, false);
      handleRateVisibleChange();
    }
  };

  const handleRateVisibleChange = () => {
    setFieldValue(CHART_SETTINGS_CHANGE_RATE_MODE, 'default');
  };

  const handleChangeScale = () => {
    if (!isWeekStartDayVisible()) {
      setFieldValue(CHART_SETTINGS_WEEK_START_DAY, START_DAY_MON);
    }
  };

  const isChangeRateModeVisible = () => {
    return values[CHART_SETTINGS_INCLUDE_CHANGE_RATE] && isChangeRateVisible();
  };

  const isLookBackDay = () => {
    return values[CHART_SETTINGS_LOOKBACK_MODE] !== '0';
  };

  const handleLookBackModeChange = () => {
    if (values.lookbackMode !== 'day') {
      /*
       * TODO: use setValues({
       *   scale:  initialValues.scale,
       *   chartGrouping:  initialValues.chartGrouping,
       *   ...values
       * })
       * */
      setFieldValue('scale', initialValues.scale);
      setFieldValue('chartGrouping', initialValues.chartGrouping);
    }
  };

  const handleClose = () => {
    if (dirty) {
      submitForm();
    }
  };

  return (
    <Popover
      anchor={
        <IconButton
          icon={<SettingsIcon />}
          tooltip={'Settings'}
          disabled={disabled}
        />
      }
      onClose={handleClose}
      PaperProps={{
        style: {
          width: '400px',
        },
      }}
    >
      <SettingsBlocksWrapper>
        <Grid container spacing={4}>
          <Grid container item spacing={2}>
            <Grid item xs={12}>
              <Typography variant={'subtitle1'}>Settings</Typography>
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={6}>
              <FormField.RadioGroup
                title={'Scale'}
                name={CHART_SETTINGS_SCALE}
                radioVertical={true}
                onChange={handleChangeScale}
                padding={'0'}
              >
                {scaleOptions.map(({ id, name }) => {
                  return (
                    <RadioGroupOption
                      key={id}
                      label={name}
                      value={id}
                      disabled={isLookBackDay()}
                    />
                  );
                })}
              </FormField.RadioGroup>
            </Grid>
            <Grid item xs={6}>
              <FormField.RadioGroup
                title={'Lookback'}
                name={CHART_SETTINGS_LOOKBACK_MODE}
                radioVertical={true}
                onChange={handleLookBackModeChange}
                padding={'0'}
              >
                {lookbackOptions.map(({ id, name }) => (
                  <RadioGroupOption key={id} label={name} value={id} />
                ))}
              </FormField.RadioGroup>
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={12}>
              <FormField.ToggleButtonGroup
                label={'Week start day'}
                padding={0}
                fullWidth={true}
                disabled={isWeekStartDayVisible()}
                name={CHART_SETTINGS_WEEK_START_DAY}
                exclusive={true}
                options={weekStartOptions}
                enforceValue={true}
              />
            </Grid>
          </Grid>
          <Grid container item spacing={2}>
            <Grid item xs={6}>
              <FormField.RadioGroup
                title={'Output'}
                name={CHART_SETTINGS_OUTPUT}
                radioVertical={true}
                onChange={handleOutputChange}
                padding={'0'}
              >
                {outputOptions.map(({ id, name }) => (
                  <RadioGroupOption
                    key={id}
                    label={name}
                    value={id}
                    disabled={isChartBreakdownMode}
                  />
                ))}
              </FormField.RadioGroup>
              {isChangeRateVisible() ? (
                <FormField.Checkbox
                  name={CHART_SETTINGS_INCLUDE_CHANGE_RATE}
                  label={'Show change rate'}
                  onChange={handleRateVisibleChange}
                  padding={'0'}
                  disabled={pieChartMode}
                />
              ) : null}
            </Grid>
            <Grid item xs={6}>
              <FormField.RadioGroup
                title={'Deposits aggregation'}
                name={CHART_SETTINGS_CHART_GROUPING}
                radioVertical={true}
                padding={'0'}
                disabled={isLookBackDay()}
              >
                {depositsAggregationOptions.map(({ id, name }) => (
                  <RadioGroupOption
                    key={id}
                    label={name}
                    value={id}
                    disabled={isLookBackDay()}
                  />
                ))}
              </FormField.RadioGroup>
            </Grid>
          </Grid>
          {isChangeRateModeVisible() ? (
            <Grid item container spacing={2}>
              <Grid item xs={12}>
                <FormField.RadioGroup
                  title={'Change rate mode'}
                  name={CHART_SETTINGS_CHANGE_RATE_MODE}
                  radioVertical={true}
                  padding={'0'}
                >
                  {changeRateOptions.map(({ id, name }) => (
                    <RadioGroupOption key={id} label={name} value={id} />
                  ))}
                </FormField.RadioGroup>
              </Grid>
            </Grid>
          ) : null}
          <Grid item container spacing={1}>
            <Grid item xs={12}>
              <LegacyText variant={'caption'} intensity={200}>
                Tools
              </LegacyText>
            </Grid>
            <Grid item xs={12}>
              <FormField.Checkbox
                name={CHART_SETTINGS_TREND}
                label={'Trend line'}
                padding={'0'}
                disabled={pieChartMode}
              />
            </Grid>
          </Grid>
        </Grid>
      </SettingsBlocksWrapper>
    </Popover>
  );
});
